import { useEffect, useRef } from 'react';
import * as echarts from 'echarts';

interface Props {
  option: any;
  height?: number;
  /** 图表点击回调（柱/线/扇区点击，params.name 为类目名，用于下钻跳转） */
  onClick?: (params: any) => void;
}

/* 图表主题（浅色单主题；canvas 不解析 CSS 变量，一律字面量）。
 * 仅定义默认样式（轴线/分割线/文字/tooltip/图例/饼图描边），option 内显式设置项仍优先。 */
const CHART_THEME = {
  textStyle: { color: '#4e5969' },
  axisLine: { lineStyle: { color: '#e5e6eb' } },
  axisTick: { lineStyle: { color: '#e5e6eb' } },
  axisLabel: { color: '#4e5969' },
  splitLine: { lineStyle: { color: '#e5e6eb' } },
  legend: { textStyle: { color: '#4e5969' } },
  tooltip: {
    backgroundColor: '#ffffff',
    borderColor: '#e5e6eb',
    borderWidth: 1,
    textStyle: { color: '#1d2129' },
  },
  pie: { itemStyle: { borderColor: '#ffffff', borderWidth: 2 } },
};

echarts.registerTheme('bcc-light', CHART_THEME);

/**
 * 网格兜底：统一让网格把轴标签算进去（`containLabel`）。
 * 各页 option 里的 `grid.left/top/bottom` 是手写像素值，只够默认量级的刻度；
 * 一旦出现「1,500」这类宽刻度或旋转的日期标签，文字就会被画在画布外（知识分析左轴、
 * 报表中心 SLA 右轴实测均被裁掉首字符）。option 里显式写 containLabel 的以它为准。
 */
function withContainLabel(option: any) {
  if (!option || !option.grid) return option;
  const grids = Array.isArray(option.grid) ? option.grid : [option.grid];
  return { ...option, grid: grids.map((g: any) => ({ containLabel: true, ...g })) };
}

/**
 * ECharts 容器：ResizeObserver 跟随容器尺寸（抽屉展开/栅格重排/侧栏折叠）；
 * 容器隐藏（display:none / 宽 0）时延后 init，避免尺寸错误后再无补救。
 */
export default function EChart({ option, height = 320, onClick }: Props) {
  const ref = useRef<HTMLDivElement>(null);
  const chartRef = useRef<echarts.ECharts | null>(null);
  const optionRef = useRef<any>(withContainLabel(option));
  optionRef.current = withContainLabel(option);
  const onClickRef = useRef(onClick);
  onClickRef.current = onClick;

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    let ro: ResizeObserver | null = null;
    let disposed = false;

    const tryInit = () => {
      if (disposed || !el || el.clientWidth === 0 || el.clientHeight === 0) return;
      if (chartRef.current) return;
      const chart = echarts.init(el, 'bcc-light');
      chartRef.current = chart;
      if (optionRef.current) chart.setOption(optionRef.current, true);
      chart.on('click', (params: any) => onClickRef.current?.(params));
      ro?.observe(el);
    };

    ro = new ResizeObserver(() => {
      if (!chartRef.current) {
        tryInit();
        return;
      }
      chartRef.current.resize();
    });
    ro.observe(el);
    tryInit();

    return () => {
      disposed = true;
      ro?.disconnect();
      chartRef.current?.dispose();
      chartRef.current = null;
    };
  }, []);

  useEffect(() => {
    if (chartRef.current && option) {
      chartRef.current.setOption(withContainLabel(option), true);
    }
  }, [option]);

  return <div ref={ref} style={{ height, width: '100%' }} />;
}
